/**
 * OPDS Store
 * State management for OPDS catalogs and navigation
 */

import axios from 'axios';
import { OPDSClient } from '@legere/opds-client';
import type { OPDSFeed, OPDSEntry, OPDSSearchParams } from '@legere/opds-client';
import { OPDSCatalogStorage, type UserOPDSCatalog } from '../lib/opds-catalog-storage';

interface NavigationHistoryItem {
  url: string;
  title: string;
}

export interface OPDSError {
  type: 'cors' | 'network' | 'http' | 'timeout' | 'parse' | 'unknown';
  message: string;
  details?: string;
  statusCode?: number;
  url?: string;
}

class OPDSStore {
  catalogs = $state<UserOPDSCatalog[]>([]);
  currentFeed = $state<OPDSFeed | null>(null);
  navigationHistory = $state<NavigationHistoryItem[]>([]);
  loading = $state(false);
  error = $state<OPDSError | null>(null);
  searchQuery = $state('');
  isSearchMode = $state(false);

  private client: OPDSClient;

  constructor() {
    this.client = new OPDSClient({ timeout: 30000 });
    this.loadCatalogs();
  }

  /**
   * Load user catalogs from storage
   */
  loadCatalogs() {
    this.catalogs = OPDSCatalogStorage.getAll();
  }

  /**
   * Add catalog to user's collection
   */
  addCatalog(catalog: Omit<UserOPDSCatalog, 'id' | 'addedAt'>): UserOPDSCatalog {
    const newCatalog = OPDSCatalogStorage.add(catalog);
    this.loadCatalogs();
    return newCatalog;
  }

  /**
   * Remove catalog from user's collection
   */
  removeCatalog(id: string) {
    OPDSCatalogStorage.remove(id);
    this.loadCatalogs();
  }

  /**
   * Fetch OPDS feed from URL
   */
  async fetchFeed(url: string, title?: string) {
    this.loading = true;
    this.error = null;

    try {
      const feed = await this.client.fetchFeed(url);
      this.currentFeed = feed;

      // Add to navigation history
      if (title || feed.title) {
        this.navigationHistory.push({
          url,
          title: title || feed.title,
        });
      }

      return feed;
    } catch (err) {
      this.error = this.parseError(err, url);
      throw err;
    } finally {
      this.loading = false;
    }
  }

  /**
   * Navigate to catalog root
   */
  async openCatalog(catalog: UserOPDSCatalog) {
    // Update last accessed
    OPDSCatalogStorage.updateLastAccessed(catalog.id);
    this.loadCatalogs();

    // Clear navigation history and start fresh
    this.navigationHistory = [];

    return this.fetchFeed(catalog.url, catalog.name);
  }

  /**
   * Navigate back in history
   */
  async navigateBack() {
    if (this.navigationHistory.length <= 1) {
      // Clear feed and go back to catalog list
      this.currentFeed = null;
      this.navigationHistory = [];
      return;
    }

    // Remove current page
    this.navigationHistory.pop();

    // Navigate to previous page
    const previous = this.navigationHistory[this.navigationHistory.length - 1];
    if (previous) {
      // Remove from history (will be re-added by fetchFeed)
      this.navigationHistory.pop();
      await this.fetchFeed(previous.url, previous.title);
    }
  }

  /**
   * Navigate to home (clear everything)
   */
  navigateHome() {
    this.currentFeed = null;
    this.navigationHistory = [];
    this.error = null;
    this.isSearchMode = false;
    this.searchQuery = '';
  }

  /**
   * Navigate to catalog root using start link
   */
  async navigateToStart() {
    if (!this.currentFeed?.startLink) {
      return;
    }

    this.navigationHistory = [];
    await this.fetchFeed(this.currentFeed.startLink, 'Catalog Root');
  }

  /**
   * Navigate to parent feed using up link
   */
  async navigateUp() {
    if (!this.currentFeed?.upLink) {
      return;
    }

    // Remove current from history
    if (this.navigationHistory.length > 0) {
      this.navigationHistory.pop();
    }

    await this.fetchFeed(this.currentFeed.upLink);
  }

  /**
   * Search in current catalog
   */
  async search(params: OPDSSearchParams) {
    if (!this.currentFeed?.searchLink) {
      throw new Error('Search is not available for this catalog');
    }

    this.loading = true;
    this.error = null;
    this.isSearchMode = true;
    this.searchQuery = params.searchTerms || '';

    try {
      const feed = await this.client.search(this.currentFeed.searchLink, params);
      this.currentFeed = feed;

      // Add search to history
      this.navigationHistory.push({
        url: this.currentFeed.selfLink || '',
        title: `Search: ${params.searchTerms || ''}`,
      });

      return feed;
    } catch (err) {
      this.error = this.parseError(err, this.currentFeed.searchLink);
      throw err;
    } finally {
      this.loading = false;
    }
  }

  /**
   * Clear search and return to previous feed
   */
  async clearSearch() {
    this.isSearchMode = false;
    this.searchQuery = '';
    await this.navigateBack();
  }

  /**
   * Download book from OPDS entry
   */
  async downloadBook(entry: OPDSEntry): Promise<{ blob: Blob; filename: string; type: string } | null> {
    // Find acquisition link
    const acquisitionLink = entry.links.find(
      (link) =>
        link.rel?.includes('opds-spec.org/acquisition') ||
        link.rel === 'http://opds-spec.org/acquisition/open-access'
    );

    if (!acquisitionLink) {
      throw new Error('No acquisition link found');
    }

    // Fetch the book using axios
    const response = await axios.get(acquisitionLink.href, {
      responseType: 'arraybuffer',
    });

    const blob = new Blob([response.data], { type: acquisitionLink.type || response.headers['content-type'] || 'application/octet-stream' });
    const type = acquisitionLink.type || blob.type;

    // Generate filename from title
    const filename = this.generateFilename(entry.title, type);

    return { blob, filename, type };
  }

  /**
   * Generate filename from title and MIME type
   */
  private generateFilename(title: string, mimeType: string): string {
    // Clean title for filename
    const cleanTitle = title.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-');

    // Determine extension from MIME type
    const extensions: Record<string, string> = {
      'application/epub+zip': 'epub',
      'application/pdf': 'pdf',
      'application/x-mobipocket-ebook': 'mobi',
      'application/vnd.amazon.ebook': 'azw',
      'text/plain': 'txt',
      'text/html': 'html',
    };

    const ext = extensions[mimeType] || 'book';

    return `${cleanTitle}.${ext}`;
  }

  /**
   * Parse error into structured OPDSError
   */
  private parseError(err: unknown, url?: string): OPDSError {
    if (axios.isAxiosError(err)) {
      // Network errors (CORS, DNS, connection refused)
      if (err.code === 'ERR_NETWORK' || err.code === 'ECONNREFUSED') {
        return {
          type: 'cors',
          message: 'Cannot access catalog',
          details: 'This catalog does not allow browser access (CORS policy). The catalog may only be accessible from native apps or specific domains.',
          url,
        };
      }

      // Timeout errors
      if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        return {
          type: 'timeout',
          message: 'Request timed out',
          details: 'The catalog server took too long to respond. It may be slow or unavailable.',
          url,
        };
      }

      // HTTP errors (4xx, 5xx)
      if (err.response) {
        const status = err.response.status;
        let message = `HTTP ${status}`;
        let details = '';

        if (status === 401 || status === 403) {
          message = 'Access denied';
          details = 'This catalog requires authentication or you do not have permission to access it.';
        } else if (status === 404) {
          message = 'Catalog not found';
          details = 'The requested catalog does not exist or has been moved.';
        } else if (status === 429) {
          message = 'Too many requests';
          details = 'You have made too many requests. Please wait and try again later.';
        } else if (status >= 500) {
          message = 'Server error';
          details = 'The catalog server is experiencing problems. Please try again later.';
        }

        return {
          type: 'http',
          message,
          details,
          statusCode: status,
          url,
        };
      }

      // Other network errors
      return {
        type: 'network',
        message: 'Network error',
        details: err.message || 'Failed to connect to the catalog server.',
        url,
      };
    }

    // XML parsing errors
    if (err instanceof Error && err.message.includes('parse')) {
      return {
        type: 'parse',
        message: 'Invalid catalog format',
        details: 'The catalog returned invalid data. It may not be a valid OPDS catalog.',
        url,
      };
    }

    // Unknown errors
    return {
      type: 'unknown',
      message: 'Unknown error',
      details: err instanceof Error ? err.message : 'An unexpected error occurred.',
      url,
    };
  }
}

export const opdsStore = new OPDSStore();
